/// <amd-module name="Test/Analytics/TrendChartCommon/Sources/TrendChartCommonSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { TestReportSource } from "Test/Analytics/Common/Sources/TestReportSource";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { ITrendChartData } from "Test/Analytics/TrendChartCommon/Types";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import { ITfsPageService } from "Tfs/Platform/Page";

export abstract class TrendChartCommonSource extends TestReportSource {
    public _queryDefaultTestOutcomeCountAndPassRateTrendData_TestRunTable(
        testReportContext: CommonTypes.ITestReportContext
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testRunEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                CommonTypes.TrendBy.Days,
                CommonTypes.Period.Days_14
            )} ${this._getFilterByFieldsString({
                [CommonTypes.Filter.Workflow.toString()]: {
                    values: [new FilterValueItem(CommonTypes.Workflow.Build, CommonTypes.Workflow[CommonTypes.Workflow.Build])]
                }
            })})`;

            const outcomeAggrStr: string = this.getOutcomeCountAggregationString([CommonTypes.TestOutcome.Failed]);

            const groupByStr = `groupby((${this._getGroupByPeriodColumnString(CommonTypes.TrendBy.Days)}), aggregate(${outcomeAggrStr}))`;

            queryOptions.$apply = `${filterStr}/${groupByStr}`;
            queryOptions.$orderby = ` CompletedDateSK asc `;

            return this._queryDefaultTestOutcomeCountAndPassRateTrendData(queryOptions);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryDefaultTestOutcomeCountAndPassRateTrendData(queryOptions: ODataQueryOptions): Promise<ITrendChartData> {
        const entityWiseOutcomeParseFunction: Function = this._getEntityWiseOutcomeCountParseFunction(queryOptions.entityType);
        const entityWiseTotalCountParseFunction: Function = this._getEntityWiseTotalCountParseFunction(queryOptions.entityType);

        return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
            if (!data || !data.value) {
                throw new Error("Data is empty");
            }

            const defaultConfValues: CommonTypes.IReportConfiguration = {
                groupBy: CommonTypes.GroupBy.None,
                trendBy: CommonTypes.TrendBy.Days,
                period: CommonTypes.Period.Days_14,
                outcomes: [CommonTypes.TestOutcome.Failed],
                configuredFilters: {}
            };

            const chartData: ITrendChartData = {
                primaryChartData: { [this._emptyString]: [] },
                secondaryChartData: [],
                confValues: defaultConfValues,
                metricSelected: CommonTypes.Metric.ResultCountAndPassRate,
                isCachedData: data.isCachedData
            };

            // Iterate over all data
            (data.value as any[]).forEach(d => {
                const date = this._parseReturnDateIntoDisplayForm(CommonTypes.TrendBy.Days, d);

                const outcomeCount: number = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.Failed, d);

                chartData.primaryChartData[this._emptyString].push({ date: date, aggrValue: outcomeCount });

                const totalCount: number = entityWiseTotalCountParseFunction(d);
                const notExecutedCount: number = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.NotExecuted, d);
                const passedCount: number = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.Passed, d);
                const notImpactedCount: number = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.NotImpacted, d);

                if (!chartData.secondaryChartData) {
                    chartData.secondaryChartData = [];
                }
                chartData.secondaryChartData.push({
                    date: date,
                    aggrValue: this._getPassPercentage(totalCount, passedCount, notExecutedCount, notImpactedCount)
                });
            });

            return chartData;
        });
    }
}
